/**
 * "Confirm Request JSON" dialog shown before the vendor wizard's Live Test step.
 *
 * It takes the real request preview and masks credentials itself (fe-admin-portal#121),
 * so no caller can show the unmasked request by mistake. Display only: the live-test
 * request is sent from the unmasked values in VendorModal.
 */

import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import Editor from '@monaco-editor/react';
import { useMemo } from 'react';
import { requestPreviewDisplayText, type RequestPreview } from '../lib/requestPreviewMasking';

export interface RequestJsonConfirmDialogProps {
  open: boolean;
  requestPreview: RequestPreview;
  /** Connect-step secret values; masked wherever they appear in the preview. */
  secretValues: readonly string[];
  isDark: boolean;
  confirmDisabled: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export function RequestJsonConfirmDialog({
  open,
  requestPreview,
  secretValues,
  isDark,
  confirmDisabled,
  onClose,
  onConfirm,
}: RequestJsonConfirmDialogProps) {
  const displayText = useMemo(
    () => requestPreviewDisplayText(requestPreview, secretValues),
    [requestPreview, secretValues],
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Confirm Request JSON</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Review the exact request JSON before moving to Live Test. Credentials are masked.
        </Typography>
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
          <Editor
            height="360px"
            language="json"
            value={displayText}
            theme={isDark ? 'vs-dark' : 'light'}
            options={{
              readOnly: true,
              minimap: { enabled: false },
              fontSize: 12,
              lineNumbers: 'on',
              scrollBeyondLastLine: false,
              domReadOnly: true,
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose}>Back to Edit</Button>
        <Button variant="contained" onClick={onConfirm} disabled={confirmDisabled}>
          Confirm & Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
}
